import { useReducer, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import type { PolicyLimitation } from '@mls-wallet/shared-types';
import { formatLocalizedDateTimeWithShortTz } from '@/lib/formatLocalizedDateTimeWithShortTz';
import {
  AnchorPeriod,
  formatIntervalReset,
  formatAnchorDateInput,
  formatAnchorHourInput,
  getAnchorPeriodInput,
  hoursToIntervalResetInput,
  INTERVAL_RESET_UNIT_CONFIG,
  IntervalResetUnit,
  isIntervalResetUnit,
  parseTypedAnchorToUnixHourStart,
  sanitizeIntervalQuantityInput,
  validateIntervalResetInput,
} from './limitation-selector-helpers';

// ============================================================================
// MODULE-LEVEL HELPERS
// Defined outside the component to avoid recreation on every render.
// ============================================================================

/**
 * Start of the current **local** hour (minutes / seconds / ms zeroed).
 * Sets the default anchor.
 */
function floorNowToHour(): Date {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setMilliseconds(0);
  return date;
}

/** Toggle between AM/PM */
function AmPmSwitch({
  value,
  onChange,
}: {
  value: AnchorPeriod;
  onChange: (value: AnchorPeriod) => void;
}) {
  const isPm = value === AnchorPeriod.PM;
  return (
    <label className="relative inline-flex cursor-pointer items-center select-none">
      {/* Visually hidden input for accessibility */}
      <input
        id="anchor-period"
        type="checkbox"
        checked={isPm}
        onChange={() => onChange(isPm ? AnchorPeriod.AM : AnchorPeriod.PM)}
        className="peer sr-only"
        role="switch"
        aria-checked={isPm}
        aria-label={`Starting time period: ${isPm ? 'PM' : 'AM'}`}
      />
      <div
        className={cn(
          'peer flex h-8 w-24 items-center justify-between rounded-full bg-stone-600 px-3 text-sm text-white font-semibold relative transition-colors duration-200',
          'after:absolute after:top-1 after:left-1 after:h-6 after:w-10 after:rounded-full after:bg-white/40 after:transition-all after:content-[""] peer-checked:after:translate-x-12'
        )}
      >
        <span className="z-10 text-white transition-colors duration-200">
          AM
        </span>
        <span className="z-10 text-white transition-colors duration-200">
          PM
        </span>
      </div>
    </label>
  );
}

interface LimitationSelectorProps {
  value: PolicyLimitation | null;
  onValueChange: (value: PolicyLimitation | null) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  disabledReason?: string;
}

type LimitationType = 'single' | 'interval';
type LimitationScope = PolicyLimitation['initiatorScope'];

// Type guard to ensure the value is a valid limitation scope
function isLimitationScope(value: string): value is LimitationScope {
  return value === 'PER_ITEM' || value === 'ALL_ITEMS';
}

interface LimitationDraftState {
  type: LimitationType;
  limitation: PolicyLimitation;
  intervalQuantity: string;
  intervalUnit: IntervalResetUnit;
  anchorDate: string;
  anchorHour: string;
  anchorPeriod: AnchorPeriod;
}

// Actions for updating the state of the limitation dialog
type LimitationDraftAction =
  | { type: 'reset'; value: PolicyLimitation | null }
  | { type: 'setType'; value: LimitationType }
  | { type: 'setIntervalQuantity'; value: string }
  | { type: 'setIntervalUnit'; value: IntervalResetUnit }
  | { type: 'setAnchorDate'; value: string }
  | { type: 'setAnchorHour'; value: string }
  | { type: 'setAnchorPeriod'; value: AnchorPeriod }
  | {
      type: 'setScope';
      field: 'initiatorScope' | 'sourceAccountScope' | 'destinationScope';
      value: LimitationScope;
    };

/**
 * Builds the editable dialog state from the persisted limitation shape.
 *
 * The backend stores intervals as total hours, while the editor keeps the
 * user-facing quantity/unit and local date/hour fields separate.
 */
function createLimitationDraftState(
  value: PolicyLimitation | null
): LimitationDraftState {
  // Default anchor to the start of the current local hour
  const defaultTimestamp = Math.floor(floorNowToHour().getTime() / 1000);

  const defaultLimitation: PolicyLimitation = {
    hours: 24,
    anchorTimestamp: defaultTimestamp,
    // Default all scopes to the restrictive option for safer policy setup.
    initiatorScope: 'ALL_ITEMS',
    sourceAccountScope: 'ALL_ITEMS',
    destinationScope: 'ALL_ITEMS',
  };

  const limitation = value ?? defaultLimitation;
  const intervalReset = hoursToIntervalResetInput(limitation.hours);
  return {
    type: value ? 'interval' : 'single',
    limitation,
    intervalQuantity: String(intervalReset.quantity),
    intervalUnit: intervalReset.unit,
    anchorDate: formatAnchorDateInput(limitation.anchorTimestamp),
    anchorHour: formatAnchorHourInput(limitation.anchorTimestamp),
    anchorPeriod: getAnchorPeriodInput(limitation.anchorTimestamp),
  };
}

/**
 * Refreshes the state's anchor timestamp only when the typed local date/hour
 * currently forms a valid anchor. Invalid partial input stays visible without
 * overwriting the last valid summary timestamp.
 */
function updateLimitationAnchor(
  state: LimitationDraftState
): LimitationDraftState {
  const parsedAnchor = parseTypedAnchorToUnixHourStart(
    state.anchorDate,
    state.anchorHour,
    state.anchorPeriod
  );
  return parsedAnchor.success
    ? {
        ...state,
        limitation: {
          ...state.limitation,
          anchorTimestamp: parsedAnchor.anchorTimestamp,
        },
      }
    : state;
}

/**
 * Keeps the limitation window state in one place so quantity/unit changes, local
 * start inputs, and scope selectors cannot drift from the pending limitation.
 */
function limitationDraftReducer(
  state: LimitationDraftState,
  action: LimitationDraftAction
): LimitationDraftState {
  switch (action.type) {
    case 'reset':
      return createLimitationDraftState(action.value);
    case 'setType':
      return { ...state, type: action.value };
    case 'setIntervalQuantity': {
      const intervalValidation = validateIntervalResetInput(
        action.value,
        state.intervalUnit
      );
      return {
        ...state,
        intervalQuantity: action.value,
        limitation: intervalValidation.success
          ? { ...state.limitation, hours: intervalValidation.hours }
          : state.limitation,
      };
    }
    case 'setIntervalUnit': {
      const intervalValidation = validateIntervalResetInput(
        state.intervalQuantity,
        action.value
      );
      return {
        ...state,
        intervalUnit: action.value,
        limitation: intervalValidation.success
          ? { ...state.limitation, hours: intervalValidation.hours }
          : state.limitation,
      };
    }
    case 'setAnchorDate':
      return updateLimitationAnchor({ ...state, anchorDate: action.value });
    case 'setAnchorHour':
      return updateLimitationAnchor({ ...state, anchorHour: action.value });
    case 'setAnchorPeriod':
      return updateLimitationAnchor({ ...state, anchorPeriod: action.value });
    case 'setScope':
      return {
        ...state,
        limitation: {
          ...state.limitation,
          [action.field]: action.value,
        },
      };
    default:
      throw new Error(
        `Unhandled limitation draft action: ${JSON.stringify(action)}`
      );
  }
}

export function LimitationSelector({
  value,
  onValueChange,
  placeholder = 'No limit',
  className,
  disabled = false,
  disabledReason = 'Set an amount threshold to configure a limitation',
}: LimitationSelectorProps) {
  const [open, setOpen] = useState(false);

  /**
   * useReducer manages the state of the limitation dialog
   *
   * - limitationDraftReducer is the reducer function that updates the state
   * - value is the initial state
   * - createLimitationDraftState is the function that creates the initial state
   */
  const [draft, dispatchDraft] = useReducer(
    limitationDraftReducer,
    value,
    createLimitationDraftState
  );

  const intervalValidation = validateIntervalResetInput(
    draft.intervalQuantity,
    draft.intervalUnit
  );
  const anchorValidation = parseTypedAnchorToUnixHourStart(
    draft.anchorDate,
    draft.anchorHour,
    draft.anchorPeriod
  );

  // Formats the shortened display value for the limitation selector
  const getDisplayValue = () => {
    if (!value) return placeholder;

    const intervalText = formatIntervalReset(value.hours);
    const details = [];
    details.push(
      value.initiatorScope === 'PER_ITEM'
        ? 'Per initiator'
        : 'Across all initiators'
    );
    details.push(
      value.sourceAccountScope === 'PER_ITEM'
        ? 'Per source account'
        : 'Across all source accounts'
    );
    details.push(
      value.destinationScope === 'PER_ITEM'
        ? 'Per destination'
        : 'Across all destinations'
    );

    return (
      <div className="flex flex-col items-start">
        <span>Limit to time interval: {intervalText}</span>
        <span className="text-xs text-muted-foreground">
          Anchor:{' '}
          {formatLocalizedDateTimeWithShortTz(value.anchorTimestamp * 1000)}
        </span>
        <span className="text-xs text-muted-foreground">
          {details.join(' • ')}
        </span>
      </div>
    );
  };

  const handleSave = () => {
    // Case: Single transaction, no rate limit, set the value to null
    if (draft.type === 'single') {
      onValueChange(null);
    } else {
      // Case: failed validation, do not save
      if (!anchorValidation.success || !intervalValidation.success) {
        return;
      }
      onValueChange({
        ...draft.limitation,
        hours: intervalValidation.hours,
        anchorTimestamp: anchorValidation.anchorTimestamp,
      });
    }
    setOpen(false);
  };

  const handleCancel = () => {
    dispatchDraft({ type: 'reset', value });
    setOpen(false);
  };

  const handleOpen = () => {
    dispatchDraft({ type: 'reset', value });
    setOpen(true);
  };

  const handleTypeChange = (type: string) => {
    dispatchDraft({
      type: 'setType',
      value: type === 'single' ? 'single' : 'interval',
    });
  };

  const handleIntervalQuantityChange = (rawValue: string) => {
    dispatchDraft({
      type: 'setIntervalQuantity',
      value: sanitizeIntervalQuantityInput(rawValue),
    });
  };

  const handleIntervalUnitChange = (unit: IntervalResetUnit) => {
    dispatchDraft({ type: 'setIntervalUnit', value: unit });
  };

  // Ex. EDT
  const localTimeZone =
    Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(new Date(draft.limitation.anchorTimestamp * 1000))
      .find((part) => part.type === 'timeZoneName')?.value ?? '';

  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        size="lg"
        className={cn(
          'w-full justify-between text-left h-auto min-h-[2.5rem] py-2',
          'border-input',
          disabled && 'opacity-60 cursor-not-allowed',
          'text-base',
          className
        )}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className="flex-1 text-left">{getDisplayValue()}</div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {disabled ? (
        <p className="mt-2 text-xs text-muted-foreground">{disabledReason}</p>
      ) : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Configure rate limit</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <RadioGroup value={draft.type} onValueChange={handleTypeChange}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="single" id="single-transaction" />
                <Label htmlFor="single-transaction">No limit</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="interval" id="time-interval" />
                <Label htmlFor="time-interval">Limit policy usage</Label>
              </div>
            </RadioGroup>

            {draft.type === 'interval' && (
              <div className="space-y-4 pl-6 border-l-2 border-muted">
                <div className="space-y-2">
                  <Label htmlFor="interval-quantity">Usage resets every:</Label>
                  <div className="grid grid-cols-[1fr_150px] gap-2">
                    <Input
                      id="interval-quantity"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={draft.intervalQuantity}
                      onChange={(e) =>
                        handleIntervalQuantityChange(e.target.value)
                      }
                      placeholder="1"
                    />
                    <Select
                      value={draft.intervalUnit}
                      onValueChange={(value) => {
                        if (!isIntervalResetUnit(value)) {
                          throw new Error(
                            `Unknown interval reset unit: ${value}`
                          );
                        }
                        handleIntervalUnitChange(value);
                      }}
                    >
                      <SelectTrigger id="interval-unit">
                        <SelectValue placeholder="Select interval unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(IntervalResetUnit).map((unit) => (
                          <SelectItem key={unit} value={unit}>
                            {INTERVAL_RESET_UNIT_CONFIG[unit].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p
                    className={cn(
                      'text-xs',
                      intervalValidation.success
                        ? 'text-muted-foreground'
                        : 'text-destructive'
                    )}
                  >
                    {intervalValidation.success
                      ? `Allowed range: 1-${INTERVAL_RESET_UNIT_CONFIG[draft.intervalUnit].maxQuantity} ${INTERVAL_RESET_UNIT_CONFIG[draft.intervalUnit].labelPlural.toLowerCase()}`
                      : intervalValidation.error}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="anchor-date">Starting:</Label>
                  <div className="grid grid-cols-[1fr_80px_96px] gap-2">
                    <Input
                      id="anchor-date"
                      inputMode="numeric"
                      value={draft.anchorDate}
                      onChange={(e) => {
                        dispatchDraft({
                          type: 'setAnchorDate',
                          value: e.target.value,
                        });
                      }}
                      placeholder="MM/DD/YYYY"
                    />
                    <Input
                      id="anchor-hour"
                      inputMode="numeric"
                      pattern="^([1-9]|1[0-2])$"
                      value={draft.anchorHour}
                      onChange={(e) => {
                        dispatchDraft({
                          type: 'setAnchorHour',
                          value: sanitizeIntervalQuantityInput(e.target.value),
                        });
                      }}
                      placeholder="12"
                    />
                    <div className="flex items-center justify-end">
                      <AmPmSwitch
                        value={draft.anchorPeriod}
                        onChange={(period) =>
                          dispatchDraft({
                            type: 'setAnchorPeriod',
                            value: period,
                          })
                        }
                      />
                    </div>
                  </div>
                  <p
                    className={cn(
                      'text-xs',
                      anchorValidation.success
                        ? 'text-muted-foreground'
                        : 'text-destructive'
                    )}
                  >
                    {anchorValidation.success
                      ? `Uses your local timezone ${localTimeZone ? `(${localTimeZone})` : ''}`
                      : anchorValidation.error}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="transaction-initiator">
                    Transaction Initiator
                  </Label>
                  <Select
                    value={draft.limitation.initiatorScope}
                    onValueChange={(value) => {
                      if (!isLimitationScope(value)) {
                        throw new Error(`Unknown limitation scope: ${value}`);
                      }
                      dispatchDraft({
                        type: 'setScope',
                        field: 'initiatorScope',
                        value,
                      });
                    }}
                  >
                    <SelectTrigger id="transaction-initiator">
                      <SelectValue placeholder="Select initiator scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PER_ITEM">Per initiator</SelectItem>
                      <SelectItem value="ALL_ITEMS">
                        Across all initiators
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source-account">Source Account</Label>
                  <Select
                    value={draft.limitation.sourceAccountScope}
                    onValueChange={(value) => {
                      if (!isLimitationScope(value)) {
                        throw new Error(`Unknown limitation scope: ${value}`);
                      }
                      dispatchDraft({
                        type: 'setScope',
                        field: 'sourceAccountScope',
                        value,
                      });
                    }}
                  >
                    <SelectTrigger id="source-account">
                      <SelectValue placeholder="Select source account scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PER_ITEM">
                        Per source account
                      </SelectItem>
                      <SelectItem value="ALL_ITEMS">
                        Across all source accounts
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="destination">Destination</Label>
                  <Select
                    value={draft.limitation.destinationScope}
                    onValueChange={(value) => {
                      if (!isLimitationScope(value)) {
                        throw new Error(`Unknown limitation scope: ${value}`);
                      }
                      dispatchDraft({
                        type: 'setScope',
                        field: 'destinationScope',
                        value,
                      });
                    }}
                  >
                    <SelectTrigger id="destination">
                      <SelectValue placeholder="Select destination scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PER_ITEM">Per destination</SelectItem>
                      <SelectItem value="ALL_ITEMS">
                        Across all destinations
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {draft.type === 'interval' && (
              <div className="p-3 bg-accent rounded-md">
                <div className="text-sm font-medium mb-2">
                  Configuration summary:
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div>
                    • Time window: {draft.intervalQuantity || '0'}{' '}
                    {draft.intervalQuantity === '1'
                      ? INTERVAL_RESET_UNIT_CONFIG[draft.intervalUnit]
                          .labelSingular
                      : INTERVAL_RESET_UNIT_CONFIG[draft.intervalUnit]
                          .labelPlural}
                  </div>
                  <div>
                    • Starting:{' '}
                    {formatLocalizedDateTimeWithShortTz(
                      draft.limitation.anchorTimestamp * 1000
                    )}
                  </div>
                  <div>
                    • Initiator scope:{' '}
                    {draft.limitation.initiatorScope === 'PER_ITEM'
                      ? 'Per initiator'
                      : 'Across all initiators'}
                  </div>
                  <div>
                    • Source account scope:{' '}
                    {draft.limitation.sourceAccountScope === 'PER_ITEM'
                      ? 'Per source account'
                      : 'Across all source accounts'}
                  </div>
                  <div>
                    • Destination scope:{' '}
                    {draft.limitation.destinationScope === 'PER_ITEM'
                      ? 'Per destination'
                      : 'Across all destinations'}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                draft.type === 'interval' &&
                (!anchorValidation.success || !intervalValidation.success)
              }
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
